import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "./ui";
import { useCookieConsent } from "../consent/CookieConsentContext.jsx";

// The banner is fixed to the bottom edge, so a late web-font swap that changes its
// text wrapping moves its top edge and counts as layout shift. Show it once the
// weights it uses are available (or after a short cap if a font never arrives).
const BANNER_FONTS = ["400", "500", "600", "700"].map((weight) => `${weight} 14px Inter`);
const BANNER_FONT_TIMEOUT_MS = 3000;

function bannerFontsAvailable() {
  const fonts = typeof document !== "undefined" ? document.fonts : undefined;
  if (!fonts?.check || !fonts.load) return true;
  try {
    return BANNER_FONTS.every((font) => fonts.check(font));
  } catch {
    return true;
  }
}

function useBannerFontsReady(enabled = true) {
  const [fontsReady, setFontsReady] = useState(bannerFontsAvailable);
  useEffect(() => {
    if (fontsReady || !enabled) return undefined;
    let active = true;
    const done = () => active && setFontsReady(true);
    const timer = setTimeout(done, BANNER_FONT_TIMEOUT_MS);
    Promise.all(BANNER_FONTS.map((font) => document.fonts.load(font))).then(done, done);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [fontsReady, enabled]);
  return fontsReady;
}

export default function CookieBanner() {
  const {
    ready,
    bannerVisible,
    preferencesOpen,
    consent,
    openPreferences,
    closePreferences,
    acceptAll,
    denyOptional,
    savePreferences,
  } = useCookieConsent();

  const [draft, setDraft] = useState({
    functional: false,
    analytics: false,
    marketing: false,
  });

  // Sync draft when preferences panel opens or consent changes.
  // Defensively set functional to false when opening from a state where we don't
  // have a confirmed stored consent (null = no prior choice made), to guarantee
  // the checkbox is never pre-checked on first open.
  useEffect(() => {
    if (preferencesOpen) {
      setDraft({
        functional: consent?.functional ?? false,
        analytics: consent?.analytics ?? false,
        marketing: false,
      });
    }
  }, [preferencesOpen, consent]);

  const fontsReady = useBannerFontsReady(ready && bannerVisible);

  if (!ready) return null;

  return (
    <>
      {bannerVisible && fontsReady && (
        <div className="fixed inset-x-0 bottom-0 z-[90] border-t border-neutral-200 bg-white/95 backdrop-blur-md shadow-[0_-18px_40px_rgba(15,23,42,0.12)]">
          <div className="mx-auto flex max-w-6xl flex-col gap-4 px-4 py-4 sm:px-6 lg:flex-row lg:items-end lg:justify-between lg:px-8">
            <div className="max-w-3xl space-y-2">
              <p className="text-sm font-semibold text-neutral-900">Cookies och lokal lagring</p>
              <p className="text-sm leading-relaxed text-neutral-600">
                Vi använder nödvändig lagring för inloggning, varukorg och säljarkoppling. Funktionell lagring
                används för t.ex. nyligen visade produkter och sparade checkoutfält, men bara om du samtycker.
                Läs mer i vår <Link to="/cookiepolicy" className="font-medium text-primary-600 underline">cookiepolicy</Link>.
              </p>
              <p className="text-sm leading-relaxed text-neutral-600"><strong>Statistik (valfritt).</strong> Vi använder Google Analytics för att förstå hur sajten används, t.ex. vilka sidor som besöks och varifrån besökare kommer. Inga uppgifter används för annonsering.</p>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap lg:justify-end">
              <Button type="button" variant="outline" onClick={denyOptional} className="min-w-[190px]">
                Neka icke-nödvändiga
              </Button>
              <Button type="button" variant="outline" onClick={openPreferences} className="min-w-[150px]">
                Anpassa
              </Button>
              <Button type="button" variant="primary" onClick={acceptAll} className="min-w-[150px]">
                Acceptera alla
              </Button>
            </div>
          </div>
        </div>
      )}

      {preferencesOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/55 px-4 py-6">
          <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-3xl border border-neutral-200 bg-white shadow-2xl">
            <div className="border-b border-neutral-200 px-6 py-5">
              <h2 className="text-xl font-semibold text-neutral-900">Anpassa cookieinställningar</h2>
              <p className="mt-2 text-sm text-neutral-600">
                Du kan alltid ändra ditt val senare via länken i footern. Nödvändig lagring är alltid aktiv för att
                webbplatsen ska fungera.
              </p>
            </div>

            <div className="space-y-4 px-6 py-5 text-sm text-neutral-700">
              <PreferenceRow
                title="Nödvändig lagring"
                description="Krävs för inloggning, varukorg, säljarkoppling och för att komma ihåg ditt samtycke."
                checked
                disabled
              />
              <PreferenceRow
                title="Funktionell lagring"
                description="Gör det möjligt att spara nyligen visade produkter, senaste säljarkoppling och förifyllda checkoutfält mellan besök."
                checked={draft.functional}
                onChange={(checked) => setDraft((prev) => ({ ...prev, functional: checked }))}
              />
              <PreferenceRow
                title="Statistik"
                description="Vi använder Google Analytics för att förstå hur sajten används, t.ex. vilka sidor som besöks och varifrån besökare kommer. Inga uppgifter används för annonsering."
                checked={draft.analytics}
                onChange={(checked) => setDraft((prev) => ({ ...prev, analytics: checked }))}
              />
              <PreferenceRow
                title="Marknadsföring"
                description="Används inte i SalesPartner just nu. Inga marketing-pixlar aktiveras i detta steg."
                checked={false}
                disabled
              />
            </div>

            <div className="flex flex-col gap-2 border-t border-neutral-200 px-6 py-4 sm:flex-row sm:justify-between">
              <div className="flex flex-col gap-2 sm:flex-row">
                <Button type="button" variant="outline" onClick={denyOptional}>
                  Neka icke-nödvändiga
                </Button>
                <Button type="button" variant="primary" onClick={() => savePreferences(draft)}>
                  Spara val
                </Button>
              </div>
              <Button type="button" variant="ghost" onClick={closePreferences}>
                Stäng
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function PreferenceRow({ title, description, checked, disabled = false, onChange }) {
  return (
    <label className="flex items-start justify-between gap-4 rounded-2xl border border-neutral-200 bg-neutral-50 px-4 py-4">
      <div className="space-y-1">
        <div className="font-medium text-neutral-900">{title}</div>
        <p className="text-sm leading-relaxed text-neutral-600">{description}</p>
      </div>
      <span className="mt-1 inline-flex items-center">
        <input
          type="checkbox"
          className="h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
          checked={checked}
          disabled={disabled}
          onChange={(event) => onChange?.(event.target.checked)}
        />
      </span>
    </label>
  );
}
